import { ConfigServiceOld } from '../services/http/config.service';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {ApiDetails, EnvironmentService, LoggingService, HttpLoaderService, SessionService, ConfirmService } from '@tymes4-shared';
import {catchError, EMPTY, first, Observable} from 'rxjs';
import {AppUser} from '../models/app-user';
import { BackofficeConfigModel, TokenRequestModel, UserService } from '../api';
import { SeasonService } from '../api';
import { BASE_BO_CONFIG } from '../../injectors';
import { MsalService } from '@azure/msal-angular';
import { SKIP_UNAUTHORIZED_HANDLING } from '../core/http-context.tokens';

@Injectable()
export class AuthService {

  private environment: ApiDetails;

  constructor(
    @Inject(BASE_BO_CONFIG) public config: BackofficeConfigModel,
    private http: HttpClient,
    private router: Router,
    private logging: LoggingService,
    private msalService: MsalService,
    private environmentService: EnvironmentService,
    private sessionService: SessionService,
    private configService: ConfigServiceOld,
    private userService: UserService,
    private seasonService: SeasonService,
    private confirmService: ConfirmService,
    private loader: HttpLoaderService,
  ) {
    this.environment = this.environmentService.loadConfigurations();
  }

  setLoggedInUser(user: AppUser) {
    this.sessionService.setUser(user);
  }

  getLoggedInUser(): AppUser {
    const u: AppUser = new AppUser();
    const d = JSON.parse(sessionStorage.getItem('userData'));
    return u.deserialize(d);
  }

  isLoggedIn(): boolean {
    return (this.getToken != null);
  }

  getToken(): string {
    return sessionStorage.getItem('jwt');
  }

  setToken(token: string) {
    sessionStorage.setItem('jwt', token);
  }

  impersonateTicketTrigger() {

    this.loader.open();

    this.userService
        .getTicketTriggerLink(
          true,   // requireAuthentication
          false,  // longRunningOperation
          'body', // observe
          false,  // reportProgress
        {
          context: new HttpContext().set(SKIP_UNAUTHORIZED_HANDLING, true)
        }
      )
      .pipe(
        first(),
        catchError((error: HttpErrorResponse) => {

          this.loader.close();

          if (error.status === 401) {
            this.displayNoAccessToT2();
          }

          return EMPTY;
        })
      )
      .subscribe(link => {
        this.loader.close();
        window.open(link, "_blank");
    });

  }

  displayNoAccessToT2() {

    this.confirmService
    .confirm({
        title: 'GENERIC.CONFIRM.ERRORS.TITLE.TICKET-TRIGGER-NO-ACCESS-TITLE',
        message: 'GENERIC.CONFIRM.ERRORS.TITLE.TICKET-TRIGGER-NO-ACCESS-MESSAGE',
        okonly: true
    })
    .subscribe();

  }

  getSelectedChannelCode() {
    return sessionStorage.getItem('userSelectedChannelCode')
  }

  switchToChannel(selectedChannelId: number): any {
    this.userService.switchChannel(selectedChannelId).subscribe((data: TokenRequestModel) => {
      sessionStorage.setItem('userSelectedChannel', data.CurrentChannel.Id.toString());
      sessionStorage.setItem('userSelectedChannelCode', data.CurrentChannel.Code);
      this.sessionService.setToken(data.Token);
      this.router.navigate(['/dashboard']);
    });

    return selectedChannelId;
  }

  switchArchiveMode(newMode: boolean) {
    this.userService.switchArchiveMode(newMode).subscribe((data: TokenRequestModel) => {
      sessionStorage.setItem('userIsArchiveMode', data.IsArchiveMode.toString());
      this.sessionService.setToken(data.Token);
      window.location.reload();
    })

    return newMode;
  }

  setPastAndCurrentSeasonDates(): Observable<boolean>{
    return new Observable<boolean>((observer) => {
      this.seasonService.getPastSeason().subscribe((pastSeason) => {
        pastSeason.EndDate = new Date(pastSeason.EndDate)
        var pastSeasonEndDate;
        if (pastSeason.EndDate != null) {
          pastSeasonEndDate = `${("0" + pastSeason.EndDate.getDate()).slice(-2)}-${("0" + (pastSeason.EndDate.getMonth() + 1)).slice(-2)}-${pastSeason.EndDate.getFullYear()}`
        }

        this.seasonService.getCurrentSeason().subscribe((currentSeason) => {
          currentSeason.StartDate = new Date(currentSeason.StartDate)
          var currentSeasonStartDate;
          if (currentSeason.StartDate != null) {
            currentSeasonStartDate = `${("0" + currentSeason.StartDate.getDate()).slice(-2)}-${("0" + (currentSeason.StartDate.getMonth() + 1)).slice(-2)}-${currentSeason.StartDate.getFullYear()}`
          }
          this.sessionService.setPastAndCurrentSeasonDates((pastSeasonEndDate + "," + currentSeasonStartDate));
          sessionStorage.setItem('pastAndCurrentSeasonDates', (pastSeasonEndDate + "," + currentSeasonStartDate));
          observer.next(true);
          observer.complete();
        });
      });
    })
  }

  setHasArchiveDB(data) {
      this.sessionService.setHasArchiveDB(data);
      sessionStorage.setItem('hasArchiveDB', data.toString());
  }

  authenticate(username, password, tfaCode) {
    const data = `username=${username}&password=${password}&grant_type=password&scope=BO`;

    const reqHeader = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'no-auth': 'true',
      'tfa': tfaCode
    });

    const url = this.environment.apiURL + '/token';
    return this.http.post(url, data, {headers: reqHeader});
  }

  logOff() {

    this.logging.log('User logged off.');
    sessionStorage.removeItem('endpoint');
    sessionStorage.removeItem('userIsArchiveMode');
    sessionStorage.removeItem('hasArchiveDB');
    sessionStorage.removeItem('pastAndCurrentSeasonDates');
    sessionStorage.removeItem('state');
    sessionStorage.removeItem('ticketShopURL');
    sessionStorage.removeItem('userSelectedChannelCode');
    sessionStorage.removeItem('user_language');
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('userSelectedChannel');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('user_language');
    sessionStorage.removeItem('jwt');
    sessionStorage.removeItem('active_modules');
    this.configService.clearConfigCache();

    //the user could be logged in using entra. Also sign the user out
    if (this.config.EntraConfig && this.config.EntraConfig.Enabled)  {
      this.msalService.instance.logout();
    }
    else {
      this.router.navigate(['/sessions/signin']);
    }

  }

  isInArchiveMode(): boolean {
    return (sessionStorage.getItem('userIsArchiveMode') == "true");
  }

}
